import {
  FunctionsFetchError,
  FunctionsHttpError,
  FunctionsRelayError,
} from '@supabase/supabase-js'
import { USE_MOCK } from '@/config'
import { mockState } from '@/api/mock-data'
import { createClient } from '@/api/supabase'
import i18n from '@/utils/i18n'
import type { CalculateBillingResult } from '@/types/billing'
import type { OrderStatus } from '@/types/enums'
const TERMINAL: OrderStatus[] = [
  'COMPLETED',
  'CANCELLED',
  'REFUSED_BY_INSPECTOR',
  'REFUSED_BY_SELLER',
  'REFUSED_BUYOUT',
]

function mockGetBilling(orderId: string): CalculateBillingResult {
  const o = mockState.orders.find((x) => x.id === orderId)
  if (!o) throw new Error(i18n.t('apiErr.billing.orderNotFound'))

  const isFinal = TERMINAL.includes(o.status as OrderStatus)
  const visited = new Set(
    mockState.statusHistory.filter((h) => h.order_id === orderId).map((h) => h.to_status),
  )
  const tripOk = visited.has('DRIVING')
  const insOk = visited.has('INSPECTION')
  const dealOk = visited.has('BUYING')
  const delOk = visited.has('TRANSPORTATION')

  const items: CalculateBillingResult['items'] = [
    {
      service: 'trip',
      is_confirmed: tripOk,
      amount_customer: 2700,
      amount_inspector: 1000,
    },
    {
      service: 'inspection',
      is_confirmed: insOk,
      amount_customer: 4500,
      amount_inspector: 2000,
    },
    {
      service: 'deal',
      is_confirmed: dealOk,
      amount_customer: 13500,
      amount_inspector: 3000,
    },
    {
      service: 'delivery',
      is_confirmed: delOk,
      amount_customer: 4500,
      amount_inspector: 2000,
    },
  ]

  let confirmedC = 0
  let confirmedI = 0
  let forecastC = 0
  let forecastI = 0
  for (const it of items) {
    if (it.is_confirmed) {
      confirmedC += it.amount_customer
      confirmedI += it.amount_inspector
    } else if (!isFinal) {
      forecastC += it.amount_customer
      forecastI += it.amount_inspector
    }
  }

  const totals: CalculateBillingResult['totals'] = {
    confirmed_customer: confirmedC,
    confirmed_inspector: confirmedI,
    forecast_customer: isFinal ? 0 : forecastC,
    forecast_inspector: isFinal ? 0 : forecastI,
    total_customer: confirmedC + (isFinal ? 0 : forecastC),
    total_inspector: confirmedI + (isFinal ? 0 : forecastI),
    margin: 0,
    margin_confirmed: 0,
  }
  totals.margin = totals.total_customer - totals.total_inspector
  totals.margin_confirmed = totals.confirmed_customer - totals.confirmed_inspector

  return {
    items,
    idle: {
      periods: [],
      total_hours: 0,
      amount_customer: 0,
      amount_inspector: 0,
    },
    totals,
    is_final: isFinal,
  }
}

/** Разбор ошибки invoke: у FunctionsHttpError в message только общий текст, детали — в JSON тела. */
async function describeBillingInvokeError(error: unknown): Promise<string> {
  if (error instanceof FunctionsHttpError) {
    const res = error.context as Response
    try {
      const body = (await res.json()) as { error?: string }
      const code = body.error
      if (code === 'order_not_found') {
        return i18n.t('apiErr.billing.orderNotFoundCalc')
      }
      if (code === 'invalid_session') {
        return i18n.t('apiErr.billing.invalidSession')
      }
      if (code === 'missing_authorization') {
        return i18n.t('apiErr.billing.missingAuthCalc')
      }
      if (code === 'internal_error') {
        return i18n.t('apiErr.billing.internalCalc')
      }
      if (code === 'tariff_snapshot_missing') {
        return i18n.t('apiErr.billing.tariffSnapshotMissing')
      }
      if (code === 'missing_order_id' || code === 'invalid_json') {
        return i18n.t('apiErr.billing.badRequestCalc')
      }
      if (res.status === 404 && !code) {
        return i18n.t('apiErr.billing.notFoundCalc404Deploy')
      }
      return i18n.t('apiErr.billing.httpErrorCalc', {
        status: res.status,
        code: code ? `, ${code}` : '',
      })
    } catch {
      if (res.status === 404) {
        return i18n.t('apiErr.billing.notFoundCalc404')
      }
      return i18n.t('apiErr.billing.httpErrorCalcShort', { status: res.status })
    }
  }
  if (error instanceof FunctionsRelayError) {
    return i18n.t('apiErr.billing.relayCalc')
  }
  if (error instanceof FunctionsFetchError) {
    return i18n.t('apiErr.billing.fetchCalc')
  }
  if (error instanceof Error) return error.message
  return i18n.t('apiErr.billing.calcGeneric')
}

/**
 * Разбор ошибки invoke для preview-billing. Сообщения адаптированы под форму
 * «Новый заказ»: пользователь должен понять, что чинить (поле или окружение).
 */
async function describePreviewInvokeError(error: unknown): Promise<string> {
  if (error instanceof FunctionsHttpError) {
    const res = error.context as Response
    try {
      const body = (await res.json()) as { error?: string }
      const code = body.error
      if (code === 'city_inactive') {
        return i18n.t('apiErr.billing.cityInactive')
      }
      if (code === 'k2_not_found') {
        return i18n.t('apiErr.billing.k2NotFoundPreview')
      }
      if (code === 'base_missing') {
        return i18n.t('apiErr.billing.baseMissing')
      }
      if (code === 'dealer_not_found') {
        return i18n.t('apiErr.billing.dealerNotFound')
      }
      if (code === 'invalid_session') {
        return i18n.t('apiErr.billing.invalidSession')
      }
      if (code === 'missing_authorization') {
        return i18n.t('apiErr.billing.missingAuthPreview')
      }
      if (code === 'internal_error') {
        return i18n.t('apiErr.billing.internalPreview')
      }
      if (code === 'missing_field' || code === 'invalid_json') {
        return i18n.t('apiErr.billing.badRequestPreview')
      }
      if (res.status === 404 && !code) {
        return i18n.t('apiErr.billing.notFoundPreview404Deploy')
      }
      return i18n.t('apiErr.billing.httpErrorPreview', {
        status: res.status,
        code: code ? `, ${code}` : '',
      })
    } catch {
      if (res.status === 404) {
        return i18n.t('apiErr.billing.notFoundPreview404')
      }
      return i18n.t('apiErr.billing.httpErrorPreviewShort', { status: res.status })
    }
  }
  if (error instanceof FunctionsRelayError) {
    return i18n.t('apiErr.billing.relayPreview')
  }
  if (error instanceof FunctionsFetchError) {
    return i18n.t('apiErr.billing.fetchPreview')
  }
  if (error instanceof Error) return error.message
  return i18n.t('apiErr.billing.previewGeneric')
}

/**
 * Расчёт стоимости заказа (Edge Function `calculate-billing` в репо `stryzh/`).
 * JWT передаётся автоматически через supabase-js.
 */
export async function getBilling(orderId: string): Promise<CalculateBillingResult> {
  if (USE_MOCK) return mockGetBilling(orderId)

  const supabase = createClient()
  const { data, error } = await supabase.functions.invoke<CalculateBillingResult>(
    'calculate-billing',
    { body: { order_id: orderId } },
  )
  if (error) throw new Error(await describeBillingInvokeError(error))
  if (!data) throw new Error(i18n.t('apiErr.billing.emptyCalc'))
  return data
}

// ---------------------------------------------------------------------------
// Preview (форма «Новый заказ») — Edge Function `preview-billing`. §8.7.
// ---------------------------------------------------------------------------

export interface BillingPreviewInput {
  manager_id: string
  city: string
  estimated_price: number
}

function findK1Coeff(city: string): number | null {
  const row = mockState.ratesK1.find(
    (r) => r.is_active && r.city.toLowerCase() === city.toLowerCase(),
  )
  return row ? row.coefficient : null
}

function findK2Coeff(priceMln: number): {
  range_from: number
  range_to: number | null
  value: number
} | null {
  const candidates = mockState.ratesK2
    .filter((r) => r.is_active && r.range_from <= priceMln)
    .filter((r) => r.range_to === null || priceMln <= r.range_to)
    .sort((a, b) => b.range_from - a.range_from)
  const r = candidates[0]
  if (!r) return null
  return { range_from: r.range_from, range_to: r.range_to, value: r.coefficient }
}

function findK3(managerId: string): number {
  const profile = mockState.profiles.find((p) => p.id === managerId)
  if (!profile?.company_id) return 1.0
  const company = mockState.companies.find((c) => c.id === profile.company_id)
  return company?.k3 ?? 1.0
}

function mockPreviewBilling(input: BillingPreviewInput): CalculateBillingResult {
  const k1ValueOrNull = findK1Coeff(input.city)
  if (k1ValueOrNull == null) {
    throw new Error(i18n.t('apiErr.billing.cityInactive'))
  }
  const k1value: number = k1ValueOrNull
  const priceMln = Math.max(0, input.estimated_price) / 1_000_000
  const k2 = findK2Coeff(priceMln)
  if (!k2) throw new Error(i18n.t('apiErr.billing.k2NotFound'))
  const k2value = k2.value
  const k3value = findK3(input.manager_id)
  const base = mockState.ratesBase

  type Service = 'trip' | 'inspection' | 'deal' | 'delivery'

  function rawAmounts(service: Service): {
    customer: number
    inspector: number
  } {
    const map: Record<Service, [number, number]> = {
      trip: [base.trip_customer, base.trip_inspector],
      inspection: [base.inspection_customer, base.inspection_inspector],
      deal: [base.deal_customer, base.deal_inspector],
      delivery: [base.delivery_customer, base.delivery_inspector],
    }
    const [bc, bi] = map[service]
    if (service === 'trip') {
      return { customer: bc * k1value * k3value, inspector: bi * k1value }
    }
    return {
      customer: bc * k1value * k2value * k3value,
      inspector: bi * k1value * k2value,
    }
  }

  const services: Service[] = ['trip', 'inspection', 'deal', 'delivery']
  const items = services.map((service) => {
    const raw = rawAmounts(service)
    return {
      service,
      is_confirmed: false,
      amount_customer: Math.round(raw.customer),
      amount_inspector: Math.round(raw.inspector),
    }
  })

  let forecastC = 0
  let forecastI = 0
  for (const it of items) {
    forecastC += it.amount_customer
    forecastI += it.amount_inspector
  }

  return {
    items,
    idle: { periods: [], total_hours: 0, amount_customer: 0, amount_inspector: 0 },
    totals: {
      confirmed_customer: 0,
      confirmed_inspector: 0,
      forecast_customer: forecastC,
      forecast_inspector: forecastI,
      total_customer: forecastC,
      total_inspector: forecastI,
      margin: forecastC - forecastI,
      margin_confirmed: 0,
    },
    is_final: false,
  }
}

/**
 * Предварительный расчёт стоимости для формы «Новый заказ».
 *
 * Отдаёт ту же структуру `CalculateBillingResult`, что `getBilling`, с
 * `is_final = false`, `items[*].is_confirmed = false`, пустым простоем и
 * `forecast_*` агрегатами по услугам в скопе. Источник — Edge Function
 * `preview-billing` (репо `stryzh/`); на моках — локальный расчёт по тем же
 * формулам и тем же справочникам, что `mockState.rates*`.
 */
export async function getBillingPreview(
  input: BillingPreviewInput,
): Promise<CalculateBillingResult> {
  if (USE_MOCK) return mockPreviewBilling(input)

  const supabase = createClient()
  const { data, error } = await supabase.functions.invoke<CalculateBillingResult>(
    'preview-billing',
    {
      body: {
        manager_id: input.manager_id,
        city: input.city,
        estimated_price: input.estimated_price,
      },
    },
  )
  if (error) throw new Error(await describePreviewInvokeError(error))
  if (!data) throw new Error(i18n.t('apiErr.billing.emptyPreview'))
  return data
}
